import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const routeMetaMap: Record<
  string,
  {
    title: string;
    description: string;
  }
> = {
  '/company-matching': {
    title: 'Company Matching Workspace – Login Required | MotivaMatch',
    description:
      'Access AI-powered employer matching platform. Sign in to discover companies aligned with your professional skills, values, and career objectives through advanced job fit analysis.',
  },
  '/interview-prep-functional': {
    title: 'Interview Prep Workspace – Login Required | MotivaMatch',
    description:
      'Access personalized AI interview preparation tool. Sign in to practice role-specific interview questions, receive expert answer guidance, and enhance your interview performance.',
  },
  '/profile-boost-functional': {
    title: 'Profile Boost Workspace – Login Required | MotivaMatch',
    description:
      'Optimize your professional profile with tailored AI recommendations. Please sign in to continue.',
  },
  '/cv-upload': {
    title: 'Resume Upload Workspace – Login Required | MotivaMatch',
    description:
      'Upload and analyze your resume with ATS insights. Please sign in to continue.',
  },
  '/dashboard': {
    title: 'MotivaMatch Dashboard – Login Required',
    description:
      'View your personalized AI career dashboard. Please sign in to continue.',
  },
  '/mobile-dashboard': {
    title: 'MotivaMatch Mobile Dashboard – Login Required',
    description:
      'View your personalized AI career dashboard on mobile. Please sign in to continue.',
  },
  '/applications': {
    title: 'My Applications – Login Required | MotivaMatch',
    description:
      'Track your job applications and match scores. Please sign in to continue.',
  },
  '/openai-job-search': {
    title: 'AI Job Search Workspace – Login Required | MotivaMatch',
    description:
      'Discover AI-powered job matches tailored to your profile. Please sign in to continue.',
  },
};

const defaultMeta = {
  title: 'MotivaMatch – Login Required',
  description:
    'This workspace requires you to be signed in. Please log in to continue using MotivaMatch.',
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const meta = routeMetaMap[location.pathname] || defaultMeta;

  if (isLoading) {
    // Show loading spinner while checking authentication
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta name="robots" content="noindex, nofollow" />
          <link
            rel="canonical"
            href={`https://www.motivamatch.com${location.pathname}`}
          />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center px-4">
          <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl border border-orange-100 p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-purple-600 text-white mb-6">
              🔒
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Sign in required
            </h2>
            <p className="text-gray-600 mb-6">{meta.description}</p>
            <button
              onClick={() =>
                navigate('/auth', { state: { from: location }, replace: true })
              }
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-purple-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Go to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  return <>{children}</>;
};
